import moment from 'moment'
import { DateInput } from '@Interfaces/Util/DateInterface'
import { TimestampLike } from '@Interfaces/Util/FirebaseInterface'

/** Normalize Firestore Timestamp-like values to a JS Date for sorting or display. */
export function firestoreLikeValueToJsDate(value: unknown): Date {
  if (value instanceof Date) return value
  if (
    value &&
    typeof value === 'object' &&
    'toDate' in value &&
    typeof (value as { toDate?: () => Date }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate()
  }
  if (
    value &&
    typeof value === 'object' &&
    'seconds' in value &&
    typeof (value as { seconds: unknown }).seconds === 'number'
  ) {
    return new Date((value as { seconds: number }).seconds * 1000)
  }
  if (typeof value === 'string' || typeof value === 'number') return new Date(value)
  return new Date(NaN)
}

/**
 * Converts a Firestore timestamp to a JavaScript Date timestamp
 * @param value - Timestamp-like value
 * @returns Date timestamp in milliseconds or original value
 */
export function convertToDateString(value: TimestampLike): number | string {
  if (!value) return ''
  if (typeof value === 'object' && 'seconds' in value) {
    return new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1000000).getTime()
  }
  return typeof value === 'number' ? value : String(value)
}

/**
 * Formats a date to MM/DD/YYYY format
 * @param date - Date input (timestamp, Date object, or Firestore timestamp)
 * @returns Formatted date string
 */
export function formatDate(date: DateInput): string {
  if (!date) return ''

  let dateObj: Date | null = null

  if (typeof date === 'object' && 'seconds' in date) {
    dateObj = new Date(date.seconds * 1000)
  } else if (typeof date === 'object' && 'toDate' in date) {
    dateObj = date.toDate()
  } else if (date instanceof Date) {
    dateObj = date
  } else if (typeof date === 'number') {
    dateObj = new Date(date)
  } else if (typeof date === 'string') {
    dateObj = new Date(date)
  }

  if (!dateObj || isNaN(dateObj.getTime())) return ''

  return moment(dateObj).format('MM/DD/YYYY')
}

/**
 * Formats a date to YYYY-MM-DD format (for filters)
 * @param date - Date input
 * @returns Formatted date string in YYYY-MM-DD format
 */
export function formatFilterDate(date: DateInput): string {
  if (!date) return ''

  let dateObj: Date | null = null

  if (typeof date === 'object' && 'seconds' in date) {
    dateObj = new Date(date.seconds * 1000)
  } else if (typeof date === 'object' && 'toDate' in date) {
    dateObj = date.toDate()
  } else if (date instanceof Date) {
    dateObj = date
  } else if (typeof date === 'number') {
    dateObj = new Date(date)
  } else if (typeof date === 'string') {
    dateObj = new Date(date)
  }

  if (!dateObj || isNaN(dateObj.getTime())) return ''

  return moment(dateObj).format('YYYY-MM-DD')
}

/**
 * Formats a date with time to MM/DD/YY @ hh:mm a format
 * @param date - Date input
 * @returns Formatted date string with time
 */
export function formattedDateWithEmailSign(date: DateInput): string {
  if (!date) return ''

  let dateObj: Date | null = null

  if (typeof date === 'object' && 'seconds' in date) {
    dateObj = new Date(date.seconds * 1000)
  } else if (date instanceof Date) {
    dateObj = date
  } else if (typeof date === 'number') {
    dateObj = new Date(date)
  } else if (typeof date === 'string') {
    dateObj = new Date(date)
  }

  if (!dateObj || isNaN(dateObj.getTime())) return ''

  return moment(dateObj).format('MM/DD/YY @ hh:mm a')
}

/**
 * Calculates age from date of birth
 * @param date - Date of birth
 * @returns Age in years
 */
export function ageFromDOB(date: DateInput): number {
  if (!date) return 0

  let dateObj: Date | null = null

  if (typeof date === 'object' && 'toDate' in date) {
    dateObj = date.toDate()
  } else if (date instanceof Date) {
    dateObj = date
  } else if (typeof date === 'number') {
    dateObj = new Date(date)
  } else if (typeof date === 'string') {
    dateObj = new Date(date)
  }

  if (!dateObj || isNaN(dateObj.getTime())) return 0

  const currentDate = new Date()
  const birthYear = moment(dateObj).format('YYYY')
  return currentDate.getFullYear() - Number(birthYear)
}

/**
 * Calculates age from birthday timestamp
 * @param timestamp - Birthday timestamp in milliseconds
 * @returns Age in years
 */
export function getAgeFromBirthday(timestamp: number): number {
  if (!timestamp) return 0
  return parseInt(String((new Date().getTime() - timestamp) / (1000 * 60 * 60 * 24 * 365)))
}

/**
 * Converts a date string to a Date object
 * Handles format: "MM/DD/YYYY HH:MM AM/PM"
 * @param date - Date string to convert
 * @returns Date object
 */
export function toTimestamp(date: string): Date {
  const dateTimeParts = date.split(' ')

  let time: string

  if (dateTimeParts[1].indexOf(' ') === -1 && dateTimeParts[2] === undefined) {
    const theTime = dateTimeParts[1]
    const ampm = theTime.replace(/[0-9:]/g, '')
    const timeStr = theTime.replace(/[^a-zA-Z]/g, '')

    if (ampm === 'pm') {
      const timeParts = timeStr.split(':')
      if (Number(timeParts[0]) === 12) {
        time = Number(timeParts[0]) + ':' + timeParts[1] + ':00'
      } else {
        time = Number(timeParts[0]) + 12 + ':' + timeParts[1] + ':00'
      }
    } else {
      const timeParts = timeStr.split(':')
      if (Number(timeParts[0]) < 10) {
        time = '0' + Number(timeParts[0]) + ':' + timeParts[1] + ':00'
      } else {
        time = Number(timeParts[0]) + ':' + timeParts[1] + ':00'
      }
    }
  } else {
    time = '00:00:00'
  }

  const dateObj = new Date(dateTimeParts[0])
  const dayOfMonth = dateObj.getDate() < 10 ? '0' + dateObj.getDate() : dateObj.getDate()
  const yearMoDay = dateObj.getFullYear() + '-' + (dateObj.getMonth() + 1) + '-' + dayOfMonth

  return new Date(yearMoDay + 'T' + time)
}

/**
 * Calculates time difference between two dates
 * @param dateTime - Start date
 * @param end - End date
 * @returns Time difference object with value, suffix, and percentage
 */
export function getTimeDifference(
  dateTime: Date,
  end: Date
): {
  value: number
  suffix: string
  percentageDifference?: number
} {
  if (!dateTime) {
    return { value: -1, suffix: '' }
  }
  const date = dateTime.getTime()
  const diff = date - end.getTime()
  let msec = diff

  const percentageDifference = (diff / date) * 100
  if (msec < 0) {
    return { value: 0, suffix: '' }
  }
  const years = Math.floor(msec / 1000 / 60 / (60 * 24 * 365))
  msec -= years * 1000 * 60 * 60 * 24 * 365
  const days = Math.floor(msec / 1000 / 60 / (60 * 24))
  msec -= days * 1000 * 60 * 60 * 24
  const hh = Math.floor(msec / 1000 / 60 / 60)
  msec -= hh * 1000 * 60 * 60
  const mm = Math.floor(msec / 1000 / 60)
  msec -= mm * 1000 * 60
  const ss = Math.floor(msec / 1000)
  msec -= ss * 1000

  if (years > 0) {
    return { value: years, suffix: 'y', percentageDifference }
  } else if (days > 0) {
    return { value: days, suffix: 'd', percentageDifference }
  } else if (hh > 0) {
    return { value: hh, suffix: 'h', percentageDifference }
  } else if (mm > 0) {
    return { value: mm, suffix: 'm', percentageDifference }
  }

  return { value: 0, suffix: 'm', percentageDifference: 100 }
}
